import React, { useEffect, useState, useMemo } from 'react';

import { PlusOutlined } from '@ant-design/icons';
import ListPageHeader from '@shared/ListPageHeader';
import {
  Card,
  Table,
  Button,
  Modal,
  Form,
  Input,
  Tag,
  Typography,
  message,
} from 'antd';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';


import shelfTypeService from '@services/operator/shelfTypeService';

import { formatDateTime } from '../../../utils/dateTime';

const { Text } = Typography;

const ShelfTypesList = () => {
  const { t } = useTranslation();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      const result = await shelfTypeService.fetchList();
      if (result.success) {
        setData(result.data);
      }
    } catch (error) {
      console.error('Load shelf types error:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleAdd = () => {
    form.resetFields();
    setIsModalVisible(true);
  };

  const handleCancel = () => {
    setIsModalVisible(false);
    form.resetFields();
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);

      const result = await shelfTypeService.create(values);

      if (result.success) {
        message.success(t('common.message.create_success'));
        handleCancel();
        fetchData();
      } else if (result.errors) {
        try {
          const errorMessages = Object.entries(result.errors)
            .map(([field, details]) => {
              const msgs = details.map((d) =>
                typeof d === 'object'
                  ? t(`common.errors.messages.${d.message}`, d.interpolations || {})
                  : d
              );
              return `${t(`models.shelf_type.${field}`)}: ${msgs.join(', ')}`;
            })
            .join('\n');
          message.error(errorMessages);
        } catch {
          message.error(t('common.error.operation_failed'));
        }
      } else {
        message.error(t(`errors.${result.message}`, result.message));
      }
    } catch (error) {
      if (!error.errorFields) {
        console.error('Save shelf type error:', error);
        message.error(t('common.error.operation_failed'));
      }
    } finally {
      setSaving(false);
    }
  };

  const columns = useMemo(
    () => [
      {
        title: t('models.shelf_type.name'),
        dataIndex: 'name',
        key: 'name',
        render: (name, record) => {
          const desc = record.system_reserved && record.code
            ? t(`shared.shelf_types.system_codes.${record.code}`, { defaultValue: '' })
            : null;
          return (
            <div>
              <Link to={`/shelf_types/${record.id}`}>{name}</Link>
              {desc && (
                <div style={{ marginTop: 2 }}>
                  <Text type="secondary" style={{ fontSize: 12 }}>{desc}</Text>
                </div>
              )}
            </div>
          );
        },
      },
      {
        title: t('models.shelf_type.code'),
        dataIndex: 'code',
        key: 'code',
        width: 180,
        render: (code) => (code ? <code>{code}</code> : '-'),
      },
      {
        title: t('models.shelf_type.system_reserved'),
        key: 'system_reserved',
        width: 120,
        align: 'center',
        render: (_, record) =>
          record.system_reserved ? (
            <Tag color="blue">
              {t('shared.shelf_types.detail.system_reserved_badge')}
            </Tag>
          ) : null,
      },
      {
        title: t('common.created_at'),
        dataIndex: 'created_at',
        key: 'created_at',
        width: 180,
        render: (val) => (val ? formatDateTime(val) : '-'),
      },
    ],
    [t]
  );

  return (
    <div className="page-container">
      <ListPageHeader
        items={['warehouse_settings', 'warehouse_settings.shelf_types']}
        actions={
          <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
            {t('common.button.add')}
          </Button>
        }
      />
      <Card bordered={false}>
        <Table
          columns={columns}
          dataSource={data}
          rowKey="id"
          loading={loading}
          pagination={{ showSizeChanger: true }}
        />
      </Card>

      <Modal
        title={t('shared.shelf_types.form.title_new')}
        open={isModalVisible}
        onOk={handleSubmit}
        onCancel={handleCancel}
        confirmLoading={saving}
        okText={t('common.button.save')}
        cancelText={t('common.button.cancel')}
        width={500}
      >
        <Form form={form} layout="vertical" style={{ marginTop: 24 }}>
          <Form.Item
            name="name"
            label={t('models.shelf_type.name')}
            rules={[{ required: true, message: t('common.form.required') }]}
          >
            <Input placeholder={t('shared.shelf_types.form.name_placeholder')} />
          </Form.Item>
          <Form.Item
            name="code"
            label={t('models.shelf_type.code')}
          >
            <Input placeholder={t('shared.shelf_types.form.code_placeholder')} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default ShelfTypesList;
